import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FlightRequest, FlightDetail, FlightResponse } from "./Types";
import SearchBar from "./SearchBar";
import SortDropdown from "./SortDropdown";
import { getFlightRequests } from "apis/requestServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import RequestReviewTab from "./RequestReviewTab";
import DefaultAirLogo from "../../assets/images/logo.svg";
import Pagination from "../../components/Pagination";

interface AnsweredRequestProps {
  // Set when navigating here from a notification — opens that request's
  // detail modal and highlights/scrolls to its row, scanning other pages
  // if it isn't on the current one.
  targetRequestId?: number | string | null;
}

const getStopLabel = (stops: number): string => {
  if (!stops || stops <= 0) return "NON-STOP";
  return `${stops}-STOP`;
};

const getOtherCount = (flight: FlightRequest): number => {
  const total = flight.total ?? 0;
  const nonRevListed = flight.nonRevListed ?? 0;
  return Math.max(total - nonRevListed, 0);
};

// The API returns raw departure/arrival/elptim; the list display needs them
// pre-formatted. Shared by the normal fetch and the target-request page scan.
const mapFlightItem = (r: FlightRequest): FlightRequest => ({
  ...r,
  departureDate: `${new Date(r.departure).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })} - ${new Date(r.departure).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}`,
  departureTime: r.departure ? r.departure.split("T")[1].slice(0, 5) : "",
  arrivalTime: r.arrival ? r.arrival.split("T")[1].slice(0, 5) : "",
  duration: r.elptim
    ? `${parseInt(r.elptim.slice(0, -2))}h ${parseInt(r.elptim.slice(-2))}m`
    : "",
});

export const AnsweredRequest: React.FC<AnsweredRequestProps> = ({
  targetRequestId,
}) => {
  const [selectedFlight, setSelectedFlight] = useState<FlightRequest | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Row highlighted when arriving here via a notification's targetRequestId.
  const [highlightedRequestId, setHighlightedRequestId] = useState<
    number | null
  >(null);
  const [sortBy, setSortBy] = useState<string>("DepartureTime");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const { showLoader, hideLoader } = useGlobalLoader();
  const [flightRequests, setFlightRequests] = useState<FlightRequest[]>([]);
  const [flightResponse, setFlightResponse] = useState<any>();
  const [selectedFlightDetail, setSelectedFlightDetail] =
    useState<FlightDetail | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const status = "Answered";
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(flightResponse?.totalCount / pageSize)
    ) {
      setCurrentPage(newPage);
    }
  };

  // Change rows-per-page and jump back to the first page.
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Map display names to API sortBy values
  const mapSortByToApiValue = (displayName: string): string => {
    switch (displayName) {
      case "Departure Time":
        return "DepartureTime";
      case "Flight Number":
        return "FlightNumber";
      case "Route":
        return "Route";
      default:
        return "DepartureTime";
    }
  };

  // Debounce the search input so we refetch once the user pauses typing,
  // instead of firing a request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset currentPage to 1 when sortBy or the search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, debouncedSearchQuery]);

  const fetchData = useCallback(
    async () => {
      showLoader();
      try {
        const response = await getFlightRequests({
          pageNumber: currentPage,
          pageSize,
          status,
          sortBy: sortBy,
          searchText: debouncedSearchQuery.trim() || undefined,
        });
        setFlightResponse(response);
        setFlightRequests(response.items.map(mapFlightItem));
      } catch (error) {
        console.error("Error fetching flight requests:", error);
      } finally {
        setRefreshing(false);
        setTimeout(() => hideLoader(), 500);
      }
    },
    // showLoader/hideLoader are recreated on every render (not memoized in the
    // context), so they're intentionally excluded to avoid an infinite refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPage, sortBy, pageSize, debouncedSearchQuery]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData, reloadTrigger]);

  // Manual refresh — mirrors OpenRequest.tsx's handleRefresh: set the button
  // spinner directly here (synchronous, so it paints immediately) rather than
  // inside fetchData, which only runs later via the effect above once the
  // state reset below commits. Also clears the search box and returns to
  // page 1, since a "refresh" should reset the view rather than silently
  // re-run whatever filter was active; reloadTrigger guarantees a refetch
  // even when that reset is a no-op (nothing was actually active).
  const handleRefresh = () => {
    setRefreshing(true);
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setCurrentPage(1);
    setReloadTrigger((prev) => prev + 1);
  };

  const handleCardClick = (flight: FlightRequest) => {
    const flightDetail: FlightDetail = {
      logoImg: flight.airlineLogo,
      airlineId: flight.airlineId,
      airlineName: flight.airlineName,
      flightNumber: flight.flightNumber,
      departureAirport: flight.sourceCode,
      arrivalAirport: flight.destinationCode,
      departureTime: flight.departureTime,
      arrivalTime: flight.arrivalTime,
      flightDuration: flight.duration,
      requestDetailId: flight.requestDetailId,
      flightDate: new Date(flight.flightDate)
        .toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          weekday: "short",
          timeZone: "UTC",
        })
        .toUpperCase()
        .replace(/^(\w+),\s(\w+)\s(\d+)$/, "$2 $3 - $1"),
    };
    setSelectedFlight(flight);
    setSelectedFlightDetail(flightDetail);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFlight(null);
  };

  // Deep link from a notification: once the current page loads, open the
  // target request's detail modal, scanning other pages if it isn't here.
  const hasResolvedTargetRef = useRef(false);
  useEffect(() => {
    if (!targetRequestId || hasResolvedTargetRef.current) return;
    if (flightRequests.length === 0) return;

    const targetIdNum = Number(targetRequestId);
    const matchOnCurrentPage = flightRequests.find(
      (item) => Number(item.requestDetailId) === targetIdNum,
    );

    if (matchOnCurrentPage) {
      hasResolvedTargetRef.current = true;
      setHighlightedRequestId(targetIdNum);
      handleCardClick(matchOnCurrentPage);
      return;
    }

    if (!flightResponse || flightResponse.totalCount <= pageSize) {
      hasResolvedTargetRef.current = true;
      return;
    }

    hasResolvedTargetRef.current = true;
    (async () => {
      const totalPages = Math.ceil(flightResponse.totalCount / pageSize);
      for (let p = 1; p <= totalPages; p++) {
        if (p === currentPage) continue;
        try {
          const otherPageData = await getFlightRequests({
            pageNumber: p,
            pageSize,
            status,
            sortBy,
            searchText: debouncedSearchQuery.trim() || undefined,
          });
          const found = otherPageData.items?.find(
            (item) => Number(item.requestDetailId) === targetIdNum,
          );
          if (found) {
            setCurrentPage(p);
            setHighlightedRequestId(targetIdNum);
            handleCardClick(mapFlightItem(found));
            break;
          }
        } catch (scanErr) {
          console.error(
            `Error searching page ${p} for request ${targetRequestId}:`,
            scanErr,
          );
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightRequests, flightResponse, targetRequestId, pageSize]);

  // Scroll the highlighted row into view once it's actually on the page
  // (either the initial page or the one the scan above landed on).
  useEffect(() => {
    if (highlightedRequestId == null) return;
    const el = document.getElementById(
      `answered-request-${highlightedRequestId}`,
    );
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedRequestId, flightRequests]);

  const filteredFlights = useMemo(() => {
    if (!Array.isArray(flightRequests)) return [];
    // Search and sorting are both applied server-side (searchText / sortBy),
    // so filtering here would only narrow the current page.
    return flightRequests.filter((flight) => flight.status === "Answered");
  }, [flightRequests]);

  return (
    <section className="mt-6 ml-2 p-4 bg-white rounded-xl answered req-section">
      <div className="flex flex-wrap gap-3 items-center justify-between p-2 mb-1 req-header">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-lg req-breadcrumb">
          <span className="text-[#6B7280] font-bold">Requests</span>
          <span className="text-[#9CA3AF]">&gt;</span>
          <span className="text-[#1F2937] font-medium">Answered</span>
          <span className="flex items-center justify-center w-6 h-6 text-[10px] font-bold rounded-full bg-[#FBBF24] text-black">
            {flightResponse?.totalCount ?? 0}
          </span>
        </div>
        <div className="flex items-center gap-3 req-toolbar">
          {/* display:contents on desktop, a real flex item on phones — see
              .req-toolbar__search in index.css. */}
          <div className="req-toolbar__search contents">
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </div>
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 p-3 bg-[#FFFFFF] rounded-lg disabled:opacity-60 req-toolbar__btn"
            title="Refresh"
            aria-label="Refresh"
          >
            <i
              className={`st-reload text-[#000000] ${refreshing ? "animate-spin" : ""}`}
            ></i>
          </button>
          <SortDropdown
            currentSortBy={sortBy}
            onSortChange={(displayName) => {
              const apiValue = mapSortByToApiValue(displayName);
              setSortBy(apiValue);
            }}
          />
        </div>
      </div>
      <div>
        {filteredFlights.length === 0 ? (
          <div className="flex justify-center items-center h-24 w-full text-[#6B7280]">
            <p>No answered requests found.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredFlights.map((flight: any, index) => (
              <div
                key={index}
                id={`answered-request-${flight.requestDetailId}`}
                // Desktop keeps the Tailwind classes below; the phone layout
                // lives in index.css under .req-card (assets/css/style.css
                // outranks Tailwind responsive variants — see the note there).
                className={`req-card font-manrope flex items-center gap-4 px-4 py-[7px] overflow-x-auto border rounded-[6px] cursor-pointer transition-all duration-200 ease-out hover:shadow-md hover:-translate-y-0.5 ${
                  highlightedRequestId === flight.requestDetailId
                    ? "bg-yellow-50 border-[#FFBC00] ring-2 ring-[#FFBC00] shadow-md"
                    : "bg-[#F8F8F8] border-[#ECECED]"
                }`}
                onClick={() => handleCardClick(flight)}
              >
                {/* Airline */}
                <div className="req-card__airline flex items-center flex-shrink-0 w-[190px] gap-3 overflow-hidden">
                  <img
                    className="w-9 h-9 rounded-lg object-contain shrink-0"
                    src={flight.airlineLogo || DefaultAirLogo}
                    alt={flight.airlineName?.toLowerCase() || "Airline"}
                    onError={(e) =>
                      (e.currentTarget.src = "/assets/logos/air-logo.png")
                    }
                  />
                  <div className="req-card__airline-text min-w-0">
                    <p title={flight.airlineName} className="req-card__ellipsis text-black text-sm font-semibold whitespace-nowrap">{flight.airlineName}</p>
                    <p className="req-card__ellipsis text-[#737C8C] text-sm font-medium whitespace-nowrap">{flight?.flightNumber}</p>
                  </div>
                </div>

                {/* Date — shares the first row with the airline block on phones
                    and uses a short month there to fit. */}
                <div className="req-card__date flex items-center flex-shrink-0 w-[140px]">
                  <div className="flex flex-col gap-0.5">
                    <p className="text-[#737C8C] text-[11px] font-normal whitespace-nowrap">Date</p>
                    <p className="text-[#262626] text-sm font-medium whitespace-nowrap">
                      {(() => {
                        const date = new Date(flight?.flightDate);
                        const day = String(date.getUTCDate()).padStart(2, "0");
                        const year = date.getUTCFullYear();
                        const long = date.toLocaleString("en-US", { month: "long", timeZone: "UTC" });
                        const short = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
                        return (
                          <>
                            <span className="req-card__date-short">{`${day} ${short}, ${year}`}</span>
                            <span className="req-card__date-long">{`${day} ${long}, ${year}`}</span>
                          </>
                        );
                      })()}
                    </p>
                  </div>
                </div>

                {/* Available Seats — third row on a phone */}
                <div className="req-card__seats flex items-center flex-shrink-0 w-[100px]">
                  <div className="flex flex-col gap-0.5">
                    <p className="req-card__seats-label text-[#737C8C] text-[11px] font-normal whitespace-nowrap">Available Seats</p>
                    <p className="req-card__seats-value text-[#262626] text-base font-semibold whitespace-nowrap">
                      {flight.total ?? 0}
                    </p>
                  </div>
                </div>

                {/* Employee / Other */}
                <div className="req-card__counts flex items-center flex-shrink-0">
                  {/* Tighter on phones so this row holds the seat count, this
                      box and the stop badge on one line. */}
                  <div className="req-card__counts-box flex items-center gap-3 bg-[#ECF4FF] rounded-lg px-2.5 py-2">
                    <div className="flex flex-col">
                      <p className="req-card__counts-label text-[#5E5E5E] text-xs font-normal whitespace-nowrap">Employee</p>
                      <p className="text-[#262626] text-sm font-semibold">
                        {flight.nonRevListed ?? 0}
                      </p>
                    </div>
                    <div className="w-px h-5 bg-[#C5CAD4]"></div>
                    <div className="flex flex-col">
                      <p className="req-card__counts-label text-[#5E5E5E] text-xs font-normal whitespace-nowrap">Other</p>
                      <p className="text-[#262626] text-sm font-semibold">
                        {getOtherCount(flight)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Route — second row on a phone (order-3 puts it straight after
                    the airline + date row). */}
                <div className="req-card__route flex items-center flex-1 min-w-[220px] gap-2.5">
                  <div className="flex flex-col items-start gap-0.5 w-[54px] flex-shrink-0">
                    <p className="text-[#262626] text-base font-medium">{flight.sourceCode}</p>
                    <p className="text-[#737C8C] text-[11px] font-medium">{flight.departureTime}</p>
                  </div>

                  <div className="flex flex-col items-center w-full">
                    <div className="flex items-center w-full">
                      <div className="w-1.5 h-1.5 bg-[#C5CAD4] rounded-full flex-shrink-0"></div>
                      <div className="flex-1 border-b border-dashed border-[#9CA3AF] mx-1"></div>
                      <i className="st-flight-slim text-[#737C8C] text-xs mx-1"></i>
                      <div className="flex-1 border-b border-dashed border-[#9CA3AF] mx-1"></div>
                      <div className="w-1.5 h-1.5 bg-[#C5CAD4] rounded-full flex-shrink-0"></div>
                    </div>
                    <p className="text-[#737C8C] text-[11px] font-normal mt-0.5 whitespace-nowrap">{flight.duration}</p>
                  </div>

                  <div className="flex flex-col items-end gap-0.5 w-[54px] flex-shrink-0">
                    <p className="text-[#262626] text-base font-medium">{flight.destinationCode}</p>
                    <p className="text-[#737C8C] text-[11px] font-medium">{flight.arrivalTime}</p>
                  </div>
                </div>

                {/* Stop badge */}
                <div className="req-card__stop flex items-center flex-shrink-0">
                  <span className="req-card__stop-badge px-2 py-2 rounded-[5px] bg-[#FFECB3] text-black text-sm font-medium whitespace-nowrap">
                    {getStopLabel(flight.stops)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {selectedFlightDetail && (
        <RequestReviewTab
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          airlineId={Number(selectedFlightDetail.airlineId)}
          airlineName={selectedFlightDetail.airlineName}
          flightDate={selectedFlightDetail.flightDate}
          departureAirport={selectedFlightDetail.departureAirport}
          flightNumber={selectedFlightDetail.flightNumber}
          departureTime={selectedFlightDetail.departureTime}
          flightDuration={selectedFlightDetail.flightDuration}
          arrivalAirport={selectedFlightDetail.arrivalAirport}
          arrivalTime={selectedFlightDetail.arrivalTime}
          requestDetailId={selectedFlightDetail.requestDetailId}
          logoImg={selectedFlightDetail.logoImg}
        />
      )}

      {(flightResponse?.totalCount ?? 0) > 0 && (
        <section>
          <Pagination
            currentPage={currentPage}
            totalCount={flightResponse?.totalCount ?? 0}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showTotal
          />
        </section>
      )}
    </section>
  );
};

export default AnsweredRequest;
